<script lang="ts">import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { CheckIcon, RemoveIcon } from "@infinitro/icons";
import { Input } from "@infinitro/input";
import { onDestroy, onMount } from "svelte";
import { emailChecksStarted, startEmailVerify, stateEmail, stateEmailVerified, stateFormMode } from "../state";
let element;
let confirmed = false;
let valid = false;
let required = true;
let emailAddress;
let blurredEmails = [];
let verifyingEmail = false;
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
const validateEmail = (email) => {
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailPattern.test(email);
};
const onChange = () => {
	valid = element.checkValidity();
};
const onInput = () => {
	const email = element.value;
	valid = validateEmail(email);
	if (valid) {
		stateEmail.set(email);
	}
};
const onBlur = async (event) => {
	valid = element.value.length > 0 && element.checkValidity();
	if (valid) startEmailVerify(event, true);
};
stateEmail.subscribe((_value) => {
	emailAddress = _value ?? "";
	if (element) {
		element.value = _value;
		valid = element.checkValidity();
	}
	if (_value === "") {
		confirmed = false;
		valid = false;
	}
});
stateEmailVerified.subscribe((value) => {
	confirmed = value;
});
const onRemove = (event) => {
	event?.preventDefault();
	event?.stopPropagation();
	stateEmailVerified.set(false);
	stateEmail.set("");
};
const handleVerifyClick = async (event) => {
	event?.preventDefault();
	event?.stopPropagation();
	startEmailVerify(event, false);
};
stateFormMode.subscribe(async (value) => {
	if (element && !confirmed) {
		await sleep(100);
		element?.focus();
	}
});
function handleKeyDown(event) {
	if (event.key === "Enter" && !event.shiftKey && !verifyingEmail && valid && !emailChecksStarted.includes($stateEmail)) {
		event.preventDefault();
		handleVerifyClick();
	}
}
$: useAlternative4 = $stateUi.emailVerify.emailVerifyStyle === "alternative4";
$: useExpandedRankPresentation = $stateUi.layout?.useExpandedRankPresentation ?? false;
$: useFlat = $stateUi.modal?.modalStyle === "flat";
$: flatVerifyButtonStyle = useFlat ? `height: 48px; width: 71px; border-radius: 8px; background: var(--color-primary-500); opacity: ${valid ? 1 : .5};` : useAlternative4 ? "" : "height: 48px";
onMount(async () => {
	if (element && emailAddress) {
		element.value = emailAddress;
		valid = element.checkValidity();
	}
	if (element && !confirmed) {
		await sleep(100);
		element?.focus();
	}
	window.addEventListener("keydown", handleKeyDown);
});
onDestroy(() => {
	window.removeEventListener("keydown", handleKeyDown);
});
</script>

<span class="section" class:section-flat={useFlat}>
	<div class="input">
		<Input
			type="email"
			name="email"
			id="email"
			autocomplete="email"
			placeholder={`${$t(Verbiage.enter_your_email_address)}` +
				(required ? `` : ` (${$t(Verbiage.optional)})`)}
			{required}
			bind:element
			on:change={onChange}
			on:input={onInput}
			disabled={confirmed}
			customClasses={useFlat ? 'auth-flat-input' : ''}
			customBorderRadius={useFlat ? '8px' : undefined}
			inputClasses={useFlat ? 'auth-flat-input-el' : ''}
		/>
		{#if confirmed}<span class="trailing-icon"
				><button type="button" class="remove" on:click={onRemove}><RemoveIcon /></button><span
					class="check"><CheckIcon /></span
				></span
			>{/if}
	</div>
	{#if !confirmed}
		<div class="verify-container">
			<Button
				className={valid && useExpandedRankPresentation ? 'enable-verify-alternative' : ''}
				variant={useFlat ? 'accent' : useAlternative4 ? 'tertiary' : 'secondary'}
				text={$t(Verbiage.verify)}
				onClick={handleVerifyClick}
				disabled={!valid}
				styleCustom={flatVerifyButtonStyle}
				id="verifyButton"
			/>
		</div>
	{/if}
</span>

<style>
	.section {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
}

	.section-flat {
		width: 100%;
		gap: 10px;
		padding-top: 0px;
		padding-bottom: 0px;
}

	div.input {
		position: relative;
		display: flex;
		height: 48px;
		width: 100%;
		align-items: center;
		white-space: nowrap;
}

	div.verify-container {
		position: relative;
}

	:global(.auth-flat-input) {
		background: var(--color-background-500) !important;
		border: 0 !important;
		height: 48px !important;
	}

	:global(.auth-flat-input::before),
	:global(.auth-flat-input::after) {
		content: none !important;
	}

	.trailing-icon {
		pointer-events: auto;
		position: absolute;
		top: 4px;
		right: 0px;
		display: flex;
		padding-right: 0.5rem;
}

	.check {
		position: absolute;
		top: 14px;
}

	.remove {
		cursor: pointer;
}

	input,
	input:disabled {
		display: block;
		height: 48px;
		width: 100%;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 1rem;
		padding-right: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-alternative-4);
}

	input:focus {
		display: block;
		width: 100%;
		border-radius: 16px;
		border-width: 1px;
		background-color: var(--color-background-100);
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	input:hover {
		background: var(--gradient-main);
}

	input:active {
		background: var(--gradient-main);
}

	input:focus-within {
		border-color: var(--color-secondary-500);
}

	.input-alternative:hover,
	.input-alternative:active,
	.input-alternative:focus,
	.input-alternative {
		background-image: unset;
	}

	input:-webkit-autofill {
		--tw-text-opacity: 1 !important;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1)) !important;
		background: var(--gradient-main) !important;
		background-color: transparent !important;
}

	input:-webkit-autofill,
	input:autofill,
	input:-internal-autofill-selected {
		--tw-text-opacity: 1 !important;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1)) !important;
		background: var(--gradient-main) !important;
		background-color: transparent !important;
}

	input:-webkit-autofill:focus {
		border-color: var(--color-secondary-500);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-main);
}

	input:-webkit-autofill:focus,
	input:autofill:focus {
		border-color: var(--color-secondary-500);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-main);
}
</style>
